import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Mail, Lock, Phone, Eye, EyeOff } from "lucide-react";

export default function Auth() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextUrl = params.get("next") || "/account";
  const { user, refresh } = useAuth();

  const [mode, setMode] = useState("login"); // login | signup | forgot
  const [showPw, setShowPw] = useState(false);

  // Login state
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");

  // Signup state
  const [su, setSu] = useState({ name: "", email: "", phone: "", password: "" });

  // Forgot state
  const [fp, setFp] = useState({ step: "email", email: "", otp: "", new_password: "" });

  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) nav(nextUrl, { replace: true }); }, [user, nav, nextUrl]);

  const doLogin = async (e) => {
    e.preventDefault();
    if (!identifier || !password) return toast.error("Enter your email/phone and password");
    setBusy(true);
    try {
      await api.post("/auth/login", { identifier, password });
      await refresh();
      toast.success("Welcome back");
      nav(nextUrl, { replace: true });
    } catch (err) { toast.error(err?.response?.data?.detail || "Sign-in failed"); }
    setBusy(false);
  };

  const doSignup = async (e) => {
    e.preventDefault();
    if (!su.email || !su.password) return toast.error("Email and password are required");
    if (su.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/auth/signup", su);
      await refresh();
      toast.success("Account created — welcome!");
      nav(nextUrl, { replace: true });
    } catch (err) { toast.error(err?.response?.data?.detail || "Sign-up failed"); }
    setBusy(false);
  };

  const sendReset = async () => {
    if (!fp.email) return toast.error("Enter your email");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: fp.email });
      setFp({ ...fp, step: "otp" });
      toast.success("If that email exists, a reset code was sent");
    } catch { toast.error("Please try again"); }
    setBusy(false);
  };

  const doReset = async () => {
    if (fp.otp.length !== 6 || fp.new_password.length < 8) return toast.error("Enter the 6-digit code and a new password (8+ chars)");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email: fp.email, otp: fp.otp, new_password: fp.new_password });
      toast.success("Password updated — you can sign in now");
      setMode("login"); setIdentifier(fp.email); setPassword("");
      setFp({ step: "email", email: "", otp: "", new_password: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
    setBusy(false);
  };

  const googleSignIn = () => {
    // Preserve `next` across the Google round-trip so the user lands back
    // where they came from (e.g. /checkout) rather than /account.
    const nextParam = nextUrl && nextUrl !== "/account" ? `?next=${encodeURIComponent(nextUrl)}` : "";
    const redirectUrl = window.location.origin + "/auth/callback" + nextParam;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="text-center">
        <div className="eyebrow">Welcome</div>
        <h1 className="mt-3 font-display text-4xl text-ink-900">
          {mode === "signup" ? "Create your IMMUSKI account" : mode === "forgot" ? "Reset your password" : "Sign in to IMMUSKI"}
        </h1>
      </div>

      {mode !== "forgot" && (
        <div className="mt-8 flex border hairline">
          <button onClick={() => setMode("login")} className={"flex-1 py-3 text-xs uppercase tracking-widest " + (mode === "login" ? "bg-ink-900 text-white" : "bg-white text-ink-700")} data-testid="tab-login">Sign in</button>
          <button onClick={() => setMode("signup")} className={"flex-1 py-3 text-xs uppercase tracking-widest " + (mode === "signup" ? "bg-ink-900 text-white" : "bg-white text-ink-700")} data-testid="tab-signup">Create account</button>
        </div>
      )}

      <div className="mt-6 border hairline p-8 bg-white">
        {mode === "login" && (
          <form onSubmit={doLogin} className="space-y-4">
            <div><Label>Email or phone</Label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} className="rounded-none h-12 pl-9" data-testid="login-identifier" />
              </div>
            </div>
            <div><Label>Password</Label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none h-12 pl-9 pr-9" data-testid="login-password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500">
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={() => setMode("forgot")} className="text-xs link-underline text-ink-700" data-testid="forgot-link">Forgot password?</button>
            </div>
            <button type="submit" disabled={busy} className="w-full btn-primary-ink py-3 text-xs uppercase tracking-[0.28em] disabled:opacity-50" data-testid="login-submit">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}

        {mode === "signup" && (
          <form onSubmit={doSignup} className="space-y-4">
            <div><Label>Full name</Label><Input value={su.name} onChange={(e) => setSu({ ...su, name: e.target.value })} className="rounded-none h-12" data-testid="signup-name" /></div>
            <div><Label>Email*</Label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                <Input type="email" value={su.email} onChange={(e) => setSu({ ...su, email: e.target.value })} className="rounded-none h-12 pl-9" data-testid="signup-email" />
              </div>
            </div>
            <div><Label>Phone (optional)</Label>
              <div className="relative">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                <Input value={su.phone} onChange={(e) => setSu({ ...su, phone: e.target.value })} placeholder="+91 …" className="rounded-none h-12 pl-9" data-testid="signup-phone" />
              </div>
            </div>
            <div><Label>Password* (8+ characters)</Label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                <Input type={showPw ? "text" : "password"} value={su.password} onChange={(e) => setSu({ ...su, password: e.target.value })} className="rounded-none h-12 pl-9 pr-9" data-testid="signup-password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500">
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={busy} className="w-full btn-primary-ink py-3 text-xs uppercase tracking-[0.28em] disabled:opacity-50" data-testid="signup-submit">
              {busy ? "Creating…" : "Create account"}
            </button>
          </form>
        )}

        {mode === "forgot" && (
          <div className="space-y-4">
            {fp.step === "email" && (
              <>
                <p className="text-sm text-ink-700">Enter your email and we'll send a 6-digit reset code.</p>
                <div><Label>Email</Label><Input type="email" value={fp.email} onChange={(e) => setFp({ ...fp, email: e.target.value })} className="rounded-none h-12" data-testid="forgot-email" /></div>
                <button onClick={sendReset} disabled={busy} className="w-full btn-primary-ink py-3 text-xs uppercase tracking-[0.28em] disabled:opacity-50" data-testid="forgot-send">
                  {busy ? "Sending…" : "Send reset code"}
                </button>
              </>
            )}
            {fp.step === "otp" && (
              <>
                <p className="text-sm text-ink-700">Enter the 6-digit code sent to <span className="font-medium">{fp.email}</span> and choose a new password.</p>
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={fp.otp} onChange={(v) => setFp({ ...fp, otp: v })} data-testid="forgot-otp">
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="rounded-none border-ink-900/30 w-9 h-11 sm:w-11 sm:h-12" />)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <div><Label>New password (8+ characters)</Label><Input type="password" value={fp.new_password} onChange={(e) => setFp({ ...fp, new_password: e.target.value })} className="rounded-none h-12" data-testid="forgot-new-password" /></div>
                <button onClick={doReset} disabled={busy} className="w-full btn-primary-ink py-3 text-xs uppercase tracking-[0.28em] disabled:opacity-50" data-testid="forgot-submit">
                  {busy ? "Updating…" : "Reset password"}
                </button>
              </>
            )}
            <button onClick={() => { setMode("login"); setFp({ step: "email", email: "", otp: "", new_password: "" }); }} className="w-full text-xs text-ink-500 hover:text-ink-900">
              ← Back to sign in
            </button>
          </div>
        )}

        <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-ink-500">
          <div className="flex-1 border-t hairline" /> or <div className="flex-1 border-t hairline" />
        </div>

        <button onClick={googleSignIn} className="w-full border hairline bg-white py-3 text-sm text-ink-900 hover:bg-cream-200 flex items-center justify-center gap-3" data-testid="auth-google">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Continue with Google
        </button>
      </div>
    </div>
  );
}
